import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownComponent, MarkdownModule, MarkdownService } from 'ngx-markdown';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { PropertyService } from '../services/property.service';
import { take } from 'rxjs/operators';
import { NzImageService } from 'ng-zorro-antd/image';
import { Subscription, firstValueFrom } from 'rxjs';

type ChatRole = 'user' | 'assistant';

type HospitalityVote = 'up' | 'down';

interface ChatMessage {
  id: number;
  role: ChatRole;
  content: string;
  turnId?: string;
  votes: { up: number; down: number };
  userVote?: HospitalityVote;
  voteLoading?: boolean;
  isGreeting?: boolean;
}

interface DownvoteDialogState {
  open: boolean;
  selectedReason?: string;
  customReason?: string;
}

@Component({
  selector: 'app-hospitality-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NzSpinModule, NzIconModule, NzMessageModule, MarkdownModule],
  templateUrl: './hospitality-chat-widget.component.html',
  styleUrls: ['./hospitality-chat-widget.component.less']
})
export class HospitalityChatWidgetComponent implements AfterViewInit, OnDestroy {
  private static idCounter = 0;

  @Input() companyId?: string;
  @Input() propertyId?: string;

  @Input()
  set selectedLanguage(language: string) {
    if (!language || language === this.language) {
      return;
    }
    this.language = language;
    this.loadGreeting();
  }

  @Input()
  set themeColor(color: string | undefined) {
    this._themeColor = color || '#1890ff';
  }

  @Input() hideBubble = false;

  get themeColor(): string {
    return this._themeColor;
  }

  @ViewChild('bubble', { static: true }) bubbleRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('widgetRoot', { static: true }) rootRef!: ElementRef<HTMLElement>;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren(MarkdownComponent) assistantMarkdownComponents!: QueryList<MarkdownComponent>;

  isOpen = false;
  isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  inputValue = '';
  errorMessage = '';

  messages: ChatMessage[] = [];

  loading = false;

  mobilePosition = { x: 16, y: 16 };
  readonly downvoteReasons = [
    { key: 'outdated', translationKey: 'HOSPITALITY_CHAT.DOWNVOTE_REASON_OUTDATED' },
    { key: 'missing', translationKey: 'HOSPITALITY_CHAT.DOWNVOTE_REASON_MISSING' },
    { key: 'inaccurate', translationKey: 'HOSPITALITY_CHAT.DOWNVOTE_REASON_INACCURATE' },
    { key: 'not_relevant', translationKey: 'HOSPITALITY_CHAT.DOWNVOTE_REASON_NOT_RELEVANT' },
    { key: 'other', translationKey: 'HOSPITALITY_CHAT.DOWNVOTE_REASON_OTHER' },
  ];
  private dragging = false;
  private pointerId?: number;
  private dragOffset = { x: 0, y: 0 };
  private didDrag = false;
  private bubbleResizeObserver?: ResizeObserver;
  private markdownChangesSubscription?: Subscription;
  private pendingContentBinding?: number;
  private readonly assistantLinkCleanups = new Map<HTMLElement, () => void>();
  private readonly assistantSignedUrlCache = new Map<string, string>();
  private readonly assistantSigningPromises = new Map<string, Promise<string>>();
  private readonly imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);
  private readonly documentExtensions = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']);

  private greetingText = '';
  private greetingMessage?: ChatMessage;
  private language = 'de';
  private _themeColor = '#1890ff';
  private viewInitAnimationFrame?: number;
  downvoteDialogs: Record<string, DownvoteDialogState | undefined> = {};

  constructor(
    private propertyService: PropertyService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private messageService: NzMessageService,
    private markdownService: MarkdownService,
    private imageService: NzImageService,
  ) {
    this.language = this.translate.currentLang || 'de';
    this.configureMarkdownRenderer();
    this.loadGreeting();
    if (typeof window !== 'undefined') {
      this.mobilePosition = this.computeDefaultMobilePosition();
    }
  }

  ngAfterViewInit(): void {
    if (this.isMobile) {
      if (typeof window !== 'undefined') {
        this.viewInitAnimationFrame = window.requestAnimationFrame(() => {
          this.viewInitAnimationFrame = undefined;
          this.resetBubblePosition();
        });
      } else {
        this.resetBubblePosition();
      }
    }
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const bubbleElement = this.bubbleRef?.nativeElement;
      if (bubbleElement) {
        this.bubbleResizeObserver = new ResizeObserver(() => {
          if (this.isMobile) {
            this.keepBubbleWithinViewport();
          }
        });
        this.bubbleResizeObserver.observe(bubbleElement);
      }
    }

    if (this.assistantMarkdownComponents) {
      this.markdownChangesSubscription = this.assistantMarkdownComponents.changes.subscribe(() => {
        this.scheduleAssistantContentBinding();
      });
      this.scheduleAssistantContentBinding();
    }
  }

  ngOnDestroy(): void {
    this.bubbleResizeObserver?.disconnect();
    if (typeof window !== 'undefined' && this.viewInitAnimationFrame !== undefined) {
      window.cancelAnimationFrame(this.viewInitAnimationFrame);
    }
    this.markdownChangesSubscription?.unsubscribe();
    this.clearAssistantContentBindingTimer();
    this.assistantSigningPromises.clear();
    this.assistantSignedUrlCache.clear();
    this.teardownAssistantContentInteractions();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;
    if (!wasMobile && this.isMobile) {
      this.resetBubblePosition();
    }
    if (this.isMobile) {
      this.keepBubbleWithinViewport();
    }
    if (wasMobile && !this.isMobile) {
      this.didDrag = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;

    // 1) Ignore clicks that originate inside the chat
    if (this.isClickInsideChat(event.target)) return;

    // 2) Ignore clicks that are closing / interacting with an overlay
    if (this.isClickInsideOverlay(event.target)) return;

    // 3) Otherwise it's a real outside click → close chat
    this.closeWidget();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEsc(event: KeyboardEvent): void {
    if (!this.isOpen) return;

    // If an overlay is open, let it own ESC without closing the chat
    if (this.isAnyOverlayOpen()) return;

    this.closeWidget();
  }

  onBubbleClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.didDrag) {
      this.didDrag = false;
      return;
    }
    if (this.isOpen) {
      this.closeWidget();
    } else {
      this.openWidget();
    }
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.isMobile) {
      return;
    }
    this.dragging = true;
    this.didDrag = false;
    this.pointerId = event.pointerId;
    const bubbleElement = this.bubbleRef?.nativeElement;
    if (!bubbleElement) {
      return;
    }
    const rect = bubbleElement.getBoundingClientRect();
    this.dragOffset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    bubbleElement.setPointerCapture(this.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragging || !this.isMobile) {
      return;
    }
    event.preventDefault();
    const bubbleElement = this.bubbleRef?.nativeElement;
    if (!bubbleElement) {
      return;
    }
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bubbleRect = bubbleElement.getBoundingClientRect();

    let nextX = event.clientX - this.dragOffset.x;
    let nextY = event.clientY - this.dragOffset.y;

    const minX = 12;
    const minY = 12;
    const maxX = viewportWidth - bubbleRect.width - minX;
    const maxY = viewportHeight - bubbleRect.height - minY;

    nextX = Math.max(minX, Math.min(nextX, maxX));
    nextY = Math.max(minY, Math.min(nextY, maxY));

    ({ x: nextX, y: nextY } = this.adjustPositionForLanguageSelector(
      { x: nextX, y: nextY },
      { width: bubbleRect.width, height: bubbleRect.height },
      { width: viewportWidth, height: viewportHeight },
    ));

    if (Math.abs(nextX - this.mobilePosition.x) > 2 || Math.abs(nextY - this.mobilePosition.y) > 2) {
      this.didDrag = true;
    }

    this.mobilePosition = { x: nextX, y: nextY };
    this.cdr.markForCheck();
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    const bubbleElement = this.bubbleRef?.nativeElement;
    if (bubbleElement && this.pointerId !== undefined) {
      bubbleElement.releasePointerCapture(this.pointerId);
    }
    this.pointerId = undefined;
  }

  onPointerCancel(): void {
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    const bubbleElement = this.bubbleRef?.nativeElement;
    if (bubbleElement && this.pointerId !== undefined) {
      bubbleElement.releasePointerCapture(this.pointerId);
    }
    this.pointerId = undefined;
  }

  openWidget(): void {
    this.isOpen = true;
    if (!this.greetingMessage && this.greetingText) {
      this.greetingMessage = this.pushMessage('assistant', this.greetingText, true);
    }
    setTimeout(() => this.scrollToBottom(), 0);
  }

  closeWidget(): void {
    this.isOpen = false;
  }

  sendQuestion(): void {
    if (!this.companyId || !this.propertyId) {
      this.errorMessage = this.translate.instant('HOSPITALITY_CHAT.ERROR_GENERIC');
      return;
    }
    const trimmed = this.inputValue.trim();
    if (!trimmed || this.loading) {
      return;
    }

    this.errorMessage = '';
    this.loading = true;

    this.pushMessage('user', trimmed);
    setTimeout(() => this.scrollToBottom(), 0);
    this.inputValue = '';

    this.propertyService
      .sendHospitalityChatMessage(this.companyId, this.propertyId, trimmed, this.language)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const assistantMessage = this.pushMessage('assistant', response.answer || '', false, response.turnId);
          assistantMessage.votes = { up: 0, down: 0 };
          setTimeout(() => this.scrollToBottom(), 0);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.errorMessage = this.translate.instant('HOSPITALITY_CHAT.ERROR_GENERIC');
        }
      });
  }

  voteUp(message: ChatMessage): void {
    this.submitVote(message, 'up');
  }

  onDownvoteButtonClick(message: ChatMessage): void {
    if (!message.turnId || !this.companyId || !this.propertyId || message.voteLoading) {
      return;
    }

    const state = message.turnId ? this.downvoteDialogs[message.turnId] : undefined;
    if (state?.open) {
      this.closeDownvoteDialog(message.turnId);
      return;
    }

    if (message.userVote === 'down') {
      return;
    }

    this.openDownvoteDialog(message.turnId);
  }

  isDownvoteDialogOpen(message: ChatMessage): boolean {
    if (!message.turnId) {
      return false;
    }
    return !!this.downvoteDialogs[message.turnId]?.open;
  }

  selectDownvoteReason(turnId: string, reasonKey: string): void {
    const current = this.downvoteDialogs[turnId] ?? { open: true };
    this.downvoteDialogs = {
      ...this.downvoteDialogs,
      [turnId]: {
        open: true,
        selectedReason: reasonKey,
        customReason: reasonKey === 'other' ? current.customReason ?? '' : '',
      },
    };
  }

  updateDownvoteCustomReason(turnId: string, value: string): void {
    const current = this.downvoteDialogs[turnId] ?? { open: true, selectedReason: 'other' };
    this.downvoteDialogs = {
      ...this.downvoteDialogs,
      [turnId]: {
        ...current,
        open: true,
        customReason: value,
      },
    };
  }

  isOtherReasonSelected(message: ChatMessage): boolean {
    if (!message.turnId) {
      return false;
    }
    return this.downvoteDialogs[message.turnId]?.selectedReason === 'other';
  }

  canSubmitDownvote(message: ChatMessage): boolean {
    if (!message.turnId) {
      return false;
    }
    const state = this.downvoteDialogs[message.turnId];
    if (!state?.selectedReason) {
      return false;
    }
    if (state.selectedReason !== 'other') {
      return true;
    }
    return !!state.customReason?.trim();
  }

  submitDownvote(message: ChatMessage): void {
    if (!message.turnId || !this.companyId || !this.propertyId) {
      return;
    }
    const state = this.downvoteDialogs[message.turnId];
    if (!state?.selectedReason) {
      return;
    }

    const reasonText = this.resolveReasonText(state);
    if (!reasonText) {
      return;
    }

    this.submitVote(message, 'down', {
      reason: reasonText,
      onSuccess: () => {
        this.closeDownvoteDialog(message.turnId!);
        this.messageService.success(this.translate.instant('HOSPITALITY_CHAT.DOWNVOTE_THANKS'));
      },
      onError: () => {
        this.openDownvoteDialog(message.turnId!, true);
      },
    });
  }

  skipDownvote(message: ChatMessage): void {
    if (!message.turnId) {
      return;
    }
    this.submitVote(message, 'down', {
      onSuccess: () => {
        this.closeDownvoteDialog(message.turnId!);
        this.messageService.success(this.translate.instant('HOSPITALITY_CHAT.DOWNVOTE_THANKS'));
      },
      onError: () => {
        this.openDownvoteDialog(message.turnId!, true);
      },
    });
  }

  trackByMessageId(index: number, message: ChatMessage): number {
    return message.id;
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendQuestion();
    }
  }

  private pushMessage(role: ChatRole, content: string, isGreeting = false, turnId?: string): ChatMessage {
    const message: ChatMessage = {
      id: ++HospitalityChatWidgetComponent.idCounter,
      role,
      content,
      turnId,
      votes: { up: 0, down: 0 },
      isGreeting,
    };
    this.messages = [...this.messages, message];
    this.scheduleAssistantContentBinding();
    return message;
  }

  private configureMarkdownRenderer(): void {
    const renderer = this.markdownService.renderer;
    renderer.link = (href: string | null, title: string | null, text: string) => {
      if (!href) {
        return text;
      }
      const normalizedHref = this.normalizeHref(href);
      const safeHref = this.escapeAttribute(normalizedHref);
      const safeTitle = title ? ` title="${this.escapeAttribute(title)}"` : '';
      const isExternal = !!safeHref && /^(https?:)?\/\//i.test(normalizedHref);
      const target = isExternal ? ' target="_blank" rel="noopener"' : '';
      return `<a href="${safeHref}"${safeTitle}${target}>${text}</a>`;
    };

    renderer.image = (href: string | null, title: string | null, text: string) => {
      const altText = this.escapeAttribute(text || '');
      const imageTitle = title ? ` title="${this.escapeAttribute(title)}"` : '';

      if (!href) {
        return `<img src="" alt="${altText}"${imageTitle} loading="lazy" />`;
      }

      const normalizedHref = this.normalizeHref(href);
      const ext = this.extractUrlExtension(normalizedHref);
      const safeHref = this.escapeAttribute(normalizedHref);
      const isExternal = /^(https?:)?\/\//i.test(normalizedHref);
      const target = isExternal ? ' target="_blank" rel="noopener"' : '';

      // Image files -> real <img> wrapped in a link (previewable)
      if (this.isImageExtension(ext)) {
        const imageTag = `<img src="${safeHref}" alt="${altText}"${imageTitle} loading="lazy" />`;
        return `<a class="assistant-markdown__image-link" href="${safeHref}"${target}>${imageTag}</a>`;
      }

      // Document / non-image files -> render as a normal link, not an <img>
      if (this.isDocumentExtension(ext)) {
        const label =
          altText ||
          (safeHref.split('/').pop() || 'Document');
        return `<a class="assistant-markdown__doc-link" href="${safeHref}"${target}>${label}</a>`;
      }

      // Unknown extension: fall back to a normal link
      const label =
        altText ||
        (safeHref.split('/').pop() || safeHref);
      return `<a class="assistant-markdown__doc-link" href="${safeHref}"${target}>${label}</a>`;
    };
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  private normalizeHref(value: string): string {
    const trimmed = value.trim();
    if (!trimmed || /^javascript:/i.test(trimmed)) {
      return '#';
    }
    if (/^www\./i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  }

  private scheduleAssistantContentBinding(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.clearAssistantContentBindingTimer();
    this.pendingContentBinding = window.setTimeout(() => {
      this.pendingContentBinding = undefined;
      this.bindAssistantContentInteractions();
    }, 0);
  }

  private clearAssistantContentBindingTimer(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.pendingContentBinding !== undefined) {
      window.clearTimeout(this.pendingContentBinding);
      this.pendingContentBinding = undefined;
    }
  }

  private bindAssistantContentInteractions(): void {
    if (!this.assistantMarkdownComponents) {
      return;
    }
    this.cleanupDetachedAssistantListeners();
    this.assistantMarkdownComponents.forEach((component) => {
      const element = component?.element?.nativeElement as HTMLElement | undefined;
      if (!element) {
        return;
      }
      const anchors = Array.from(element.querySelectorAll('a')) as HTMLAnchorElement[];
      anchors.forEach((anchor) => this.bindAssistantAnchor(anchor));
    });
  }

  private cleanupDetachedAssistantListeners(): void {
    if (typeof document === 'undefined') {
      return;
    }
    this.assistantLinkCleanups.forEach((cleanup, element) => {
      if (!document.contains(element)) {
        cleanup();
        this.assistantLinkCleanups.delete(element);
      }
    });
  }

  private teardownAssistantContentInteractions(): void {
    this.assistantLinkCleanups.forEach((cleanup) => cleanup());
    this.assistantLinkCleanups.clear();
  }

  private bindAssistantAnchor(anchor: HTMLAnchorElement): void {
    if (this.assistantLinkCleanups.has(anchor)) {
      return;
    }
    const href = anchor.getAttribute('href') || '';
    if (!href || href === '#') {
      return;
    }
    anchor.setAttribute('data-original-href', href);
    const extension = this.extractUrlExtension(href);
    if (this.isImageExtension(extension)) {
      this.prepareAssistantAsset(anchor);
      const activate = () => this.previewAssistantImage(anchor);
      const clickHandler = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        activate();
      };
      const keydownHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          activate();
        }
      };
      anchor.addEventListener('click', clickHandler);
      anchor.addEventListener('keydown', keydownHandler);
      anchor.setAttribute('role', 'button');
      anchor.setAttribute('tabindex', '0');
      this.assistantLinkCleanups.set(anchor, () => {
        anchor.removeEventListener('click', clickHandler);
        anchor.removeEventListener('keydown', keydownHandler);
      });
      return;
    }

    if (this.isDocumentExtension(extension)) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener');
      this.prepareAssistantAsset(anchor);
      const activate = () => this.activateAssistantDocument(anchor);
      const clickHandler = (event: MouseEvent) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        activate();
      };
      const keydownHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          activate();
        }
      };
      anchor.addEventListener('click', clickHandler);
      anchor.addEventListener('keydown', keydownHandler);
      this.assistantLinkCleanups.set(anchor, () => {
        anchor.removeEventListener('click', clickHandler);
        anchor.removeEventListener('keydown', keydownHandler);
      });
      return;
    }

    if (/^(https?:)?\/\//i.test(href)) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener');
    }
  }

  private extractUrlExtension(url: string): string {
    const sanitized = url.split(/[?#]/)[0];
    const match = /\.([a-zA-Z0-9]+)$/.exec(sanitized);
    return match ? match[1].toLowerCase() : '';
  }

  private isImageExtension(extension: string): boolean {
    return extension ? this.imageExtensions.has(extension) : false;
  }

  private isDocumentExtension(extension: string): boolean {
    return extension ? this.documentExtensions.has(extension) : false;
  }

  private previewAssistantImage(anchor: HTMLAnchorElement): void {
    const image = anchor.querySelector('img') as HTMLImageElement | null;
    const alt = image?.getAttribute('alt') || anchor.textContent?.trim() || '';
    const candidates = [
      this.getAssistantAnchorOriginalHref(anchor),
      image?.currentSrc,
      image?.src,
      anchor.getAttribute('href'),
      anchor.href,
    ].filter((value): value is string => !!value && value !== '#');
    if (!candidates.length) {
      return;
    }
    const primarySrc = candidates[0];
    this.resolveAssistantAssetUrl(primarySrc).then((resolved) => {
      const finalSrc = resolved || primarySrc;
      if (!finalSrc || finalSrc === '#') {
        return;
      }
      if (finalSrc !== anchor.href) {
        this.setAssistantAnchorSignedHref(anchor, finalSrc);
      }
      if (image && image.src !== finalSrc) {
        image.src = finalSrc;
      }
      this.imageService.preview([
        {
          src: finalSrc,
          alt,
        },
      ]);
    });
  }

  private activateAssistantDocument(anchor: HTMLAnchorElement): void {
    const originalHref = this.getAssistantAnchorOriginalHref(anchor) || anchor.href;
    if (!originalHref || originalHref === '#') return;

    // Open a blank tab *synchronously* (keeps user-gesture)
    let newWin: Window | null = null;
    try {
      newWin = window.open('', '_blank');
      if (newWin) newWin.opener = null;
    } catch {
      newWin = null;
    }

    const finish = (finalUrl: string) => {
      if (!finalUrl || finalUrl === '#') return;

      // keep anchor updated with the signed URL
      if (finalUrl !== anchor.href) this.setAssistantAnchorSignedHref(anchor, finalUrl);

      if (newWin) {
        // we already have a tab — just navigate it
        try {
          newWin.location.replace(finalUrl);
        } catch {
          newWin.location.href = finalUrl;
        }
        return;
      }

      // Popup blocked: synthetic link click (still opens a new tab, does not replace SPA)
      const a = document.createElement('a');
      a.href = finalUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    // Resolve (sign) the URL asynchronously, then load it in the tab we opened above
    this.resolveAssistantAssetUrl(originalHref).then((resolved) => {
      const finalUrl = resolved || originalHref;
      finish(finalUrl);
    });
  }

  private getAssistantAnchorOriginalHref(anchor: HTMLAnchorElement): string {
    const original = anchor.getAttribute('data-original-href');
    return original || anchor.getAttribute('href') || anchor.href || '';
  }

  private setAssistantAnchorSignedHref(anchor: HTMLAnchorElement, url: string): void {
    anchor.setAttribute('data-signed-href', url);
    anchor.href = url;
  }

  private prepareAssistantAsset(anchor: HTMLAnchorElement): void {
    const originalHref = this.getAssistantAnchorOriginalHref(anchor);
    if (!originalHref) {
      return;
    }
    this.resolveAssistantAssetUrl(originalHref).then((signed) => {
      if (!signed || signed === originalHref) {
        return;
      }
      this.setAssistantAnchorSignedHref(anchor, signed);
      const image = anchor.querySelector('img');
      if (image instanceof HTMLImageElement && image.src !== signed) {
        image.src = signed;
      }
    });
  }

  private shouldSignAssistantAsset(url: string): boolean {
    if (!url || /[?&]X-Amz-Algorithm=/i.test(url)) {
      return false;
    }
    try {
      const base = typeof window !== 'undefined' ? window.location.href : 'https://localhost';
      const parsed = new URL(url, base);
      return /amazonaws\.com$/i.test(parsed.hostname);
    } catch (error) {
      return false;
    }
  }

  private resolveAssistantAssetUrl(url: string): Promise<string> {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      return Promise.resolve('');
    }
    if (!this.companyId || !this.propertyId || !this.shouldSignAssistantAsset(trimmed)) {
      return Promise.resolve(trimmed);
    }
    const cached = this.assistantSignedUrlCache.get(trimmed);
    if (cached) {
      return Promise.resolve(cached);
    }
    const pending = this.assistantSigningPromises.get(trimmed);
    if (pending) {
      return pending;
    }
    const signingPromise = firstValueFrom(
      this.propertyService
        .signHospitalityChatAsset(this.companyId, this.propertyId, trimmed)
        .pipe(take(1)),
    )
      .then((response) => {
        const signedUrl = response?.url;
        if (signedUrl) {
          this.assistantSignedUrlCache.set(trimmed, signedUrl);
          return signedUrl;
        }
        return trimmed;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to sign hospitality chat asset', error);
        return trimmed;
      });
    const trackedPromise = signingPromise.finally(() => {
      this.assistantSigningPromises.delete(trimmed);
    });
    this.assistantSigningPromises.set(trimmed, trackedPromise);
    return trackedPromise;
  }

  private loadGreeting(): void {
    this.translate
      .getTranslation(this.language)
      .pipe(take(1))
      .subscribe((translations) => {
        const value = translations?.HOSPITALITY_CHAT?.GREETING
          ?? this.translate.instant('HOSPITALITY_CHAT.GREETING');
        this.greetingText = value;
        if (this.greetingMessage) {
          this.greetingMessage.content = value;
          this.messages = [...this.messages];
        } else if (this.isOpen && value) {
          this.greetingMessage = this.pushMessage('assistant', value, true);
          setTimeout(() => this.scrollToBottom(), 0);
        }
      });
  }

  private computeDefaultMobilePosition(): { x: number; y: number } {
    if (typeof window === 'undefined') {
      return { x: 16, y: 16 };
    }
    const width = window.innerWidth;
    const height = window.innerHeight;
    const bubbleElement = this.bubbleRef?.nativeElement;
    const bubbleRect = bubbleElement?.getBoundingClientRect();
    const bubbleWidth = bubbleRect?.width ?? 180;
    const bubbleHeight = bubbleRect?.height ?? 72;
    const horizontalPadding = 16;
    const verticalPadding = 96;
    const basePosition = {
      x: Math.max(horizontalPadding, width - bubbleWidth - horizontalPadding),
      y: Math.max(horizontalPadding, height - bubbleHeight - verticalPadding),
    };
    return this.adjustPositionForLanguageSelector(
      basePosition,
      { width: bubbleWidth, height: bubbleHeight },
      { width, height },
    );
  }

  private resetBubblePosition(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.mobilePosition = this.computeDefaultMobilePosition();
    this.keepBubbleWithinViewport();
    this.cdr.markForCheck();
  }

  private keepBubbleWithinViewport(): void {
    if (typeof window === 'undefined' || !this.isMobile) {
      return;
    }
    const bubbleElement = this.bubbleRef?.nativeElement;
    if (!bubbleElement) {
      return;
    }
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bubbleRect = bubbleElement.getBoundingClientRect();
    const minX = 12;
    const minY = 12;
    const maxX = Math.max(minX, viewportWidth - bubbleRect.width - minX);
    const maxY = Math.max(minY, viewportHeight - bubbleRect.height - minY);
    const nextX = Math.min(Math.max(this.mobilePosition.x, minX), maxX);
    const nextY = Math.min(Math.max(this.mobilePosition.y, minY), maxY);
    const adjusted = this.adjustPositionForLanguageSelector(
      { x: nextX, y: nextY },
      { width: bubbleRect.width, height: bubbleRect.height },
      { width: viewportWidth, height: viewportHeight },
    );
    if (adjusted.x !== this.mobilePosition.x || adjusted.y !== this.mobilePosition.y) {
      this.mobilePosition = { x: adjusted.x, y: adjusted.y };
      this.cdr.markForCheck();
    }
  }

  private adjustPositionForLanguageSelector(
    position: { x: number; y: number },
    bubbleSize: { width: number; height: number },
    viewport: { width: number; height: number },
  ): { x: number; y: number } {
    if (typeof document === 'undefined') {
      return position;
    }

    const selector = document.querySelector('.floating-language-selector');
    if (!selector) {
      return position;
    }

    const selectorRect = selector.getBoundingClientRect();
    const buffer = 12;
    const minMargin = 12;

    const paddedSelector = {
      left: selectorRect.left - buffer,
      right: selectorRect.right + buffer,
      top: selectorRect.top - buffer,
      bottom: selectorRect.bottom + buffer,
    };

    const bubbleRect = {
      left: position.x,
      right: position.x + bubbleSize.width,
      top: position.y,
      bottom: position.y + bubbleSize.height,
    };

    const intersects =
      bubbleRect.left < paddedSelector.right &&
      bubbleRect.right > paddedSelector.left &&
      bubbleRect.top < paddedSelector.bottom &&
      bubbleRect.bottom > paddedSelector.top;

    if (!intersects) {
      return position;
    }

    let nextX = position.x;
    let nextY = position.y;

    const availableLeft = paddedSelector.left - minMargin;
    const availableRight = viewport.width - paddedSelector.right - minMargin;
    const availableAbove = paddedSelector.top - minMargin;
    const availableBelow = viewport.height - paddedSelector.bottom - minMargin;

    const canFitLeft = availableLeft >= bubbleSize.width;
    const canFitRight = availableRight >= bubbleSize.width;
    const canFitAbove = availableAbove >= bubbleSize.height;
    const canFitBelow = availableBelow >= bubbleSize.height;

    if (canFitLeft) {
      nextX = Math.max(minMargin, paddedSelector.left - bubbleSize.width - buffer);
    } else if (canFitRight) {
      nextX = Math.min(viewport.width - bubbleSize.width - minMargin, paddedSelector.right + buffer);
    }

    const stillHorizontallyOverlapping =
      nextX + bubbleSize.width > paddedSelector.left && nextX < paddedSelector.right;

    if (stillHorizontallyOverlapping) {
      if (canFitAbove) {
        nextY = Math.max(minMargin, paddedSelector.top - bubbleSize.height - buffer);
      } else if (canFitBelow) {
        nextY = Math.min(viewport.height - bubbleSize.height - minMargin, paddedSelector.bottom + buffer);
      }
    }

    nextX = Math.max(minMargin, Math.min(nextX, viewport.width - bubbleSize.width - minMargin));
    nextY = Math.max(minMargin, Math.min(nextY, viewport.height - bubbleSize.height - minMargin));

    const finalRect = {
      left: nextX,
      right: nextX + bubbleSize.width,
      top: nextY,
      bottom: nextY + bubbleSize.height,
    };

    const stillIntersecting =
      finalRect.left < paddedSelector.right &&
      finalRect.right > paddedSelector.left &&
      finalRect.top < paddedSelector.bottom &&
      finalRect.bottom > paddedSelector.top;

    if (stillIntersecting) {
      nextX = minMargin;
      nextY = Math.max(
        minMargin,
        Math.min(paddedSelector.top - bubbleSize.height - buffer, viewport.height - bubbleSize.height - minMargin),
      );

      if (nextY + bubbleSize.height > paddedSelector.top) {
        nextY = Math.min(
          viewport.height - bubbleSize.height - minMargin,
          Math.max(minMargin, paddedSelector.bottom + buffer),
        );
      }
    }

    nextX = Math.max(minMargin, Math.min(nextX, viewport.width - bubbleSize.width - minMargin));
    nextY = Math.max(minMargin, Math.min(nextY, viewport.height - bubbleSize.height - minMargin));

    return { x: nextX, y: nextY };
  }

  private scrollToBottom(): void {
    if (!this.scrollContainer) {
      return;
    }
    const element = this.scrollContainer.nativeElement;
    element.scrollTop = element.scrollHeight;
  }

  private openDownvoteDialog(turnId: string, preserveState = false): void {
    const existing = this.downvoteDialogs[turnId];
    const baseState: DownvoteDialogState = preserveState && existing
      ? { ...existing, open: true }
      : { open: true, selectedReason: undefined, customReason: '' };

    const nextDialogs: Record<string, DownvoteDialogState | undefined> = {};
    Object.keys(this.downvoteDialogs).forEach((key) => {
      if (key !== turnId) {
        const value = this.downvoteDialogs[key];
        nextDialogs[key] = value ? { ...value, open: false } : undefined;
      }
    });
    nextDialogs[turnId] = baseState;
    this.downvoteDialogs = nextDialogs;
  }

  private closeDownvoteDialog(turnId: string): void {
    if (!this.downvoteDialogs[turnId]) {
      return;
    }
    const updated = { ...this.downvoteDialogs };
    updated[turnId] = undefined;
    this.downvoteDialogs = updated;
  }

  private resolveReasonText(state: DownvoteDialogState): string | undefined {
    if (state.selectedReason === 'other') {
      return state.customReason?.trim();
    }
    const reasonDefinition = this.downvoteReasons.find((reason) => reason.key === state.selectedReason);
    if (!reasonDefinition) {
      return undefined;
    }
    return this.translate.instant(reasonDefinition.translationKey);
  }

  private submitVote(
    message: ChatMessage,
    vote: HospitalityVote,
    options: { reason?: string; onSuccess?: () => void; onError?: () => void } = {},
  ): void {
    if (!message.turnId || !this.companyId || !this.propertyId || message.voteLoading) {
      return;
    }

    const previousVote = message.userVote;
    const isSameVote = previousVote === vote;
    const reason = options.reason?.trim();
    const shouldResubmitSameVote = isSameVote && vote === 'down' && !!reason;

    if (isSameVote && !shouldResubmitSameVote) {
      return;
    }

    const previousVotes = message.votes ? { ...message.votes } : { up: 0, down: 0 };

    message.voteLoading = true;

    if (!message.votes) {
      message.votes = { up: 0, down: 0 };
    }

    if (!isSameVote) {
      if (previousVote) {
        message.votes[previousVote] = Math.max(0, (message.votes[previousVote] || 0) - 1);
      }
      message.votes[vote] = (message.votes[vote] || 0) + 1;
      message.userVote = vote;
    }

    this.propertyService
      .voteHospitalityChatTurn(this.companyId, this.propertyId, message.turnId, vote, reason)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response?.counts) {
            message.votes = response.counts;
          }
          message.userVote = response?.vote ?? vote;
          message.voteLoading = false;
          options.onSuccess?.();
        },
        error: () => {
          message.votes = previousVotes;
          message.userVote = previousVote;
          message.voteLoading = false;
          this.errorMessage = this.translate.instant('HOSPITALITY_CHAT.VOTE_ERROR');
          options.onError?.();
        },
      });
  }
  /** Ant/NG-Zorro & CDK overlay selectors (image preview, modal, drawers, dropdowns…) */
  private readonly overlaySelectors =
    '.ant-image-preview, .ant-image-preview-root, ' +
    '.ant-modal-wrap, .ant-drawer, ' +
    '.cdk-overlay-pane, .cdk-overlay-container, ' +
    '.ant-dropdown, .ant-select-dropdown';

  /** Is this event target inside the chat widget? */
  private isClickInsideChat(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    return !!el && !!this.rootRef?.nativeElement.contains(el);
  }

  /** Is this event target inside any overlay? */
  private isClickInsideOverlay(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    return !!el && !!el.closest(this.overlaySelectors);
  }

  /** Is any overlay currently mounted/open? */
  private isAnyOverlayOpen(): boolean {
    return !!document.querySelector(this.overlaySelectors);
  }

}
